import { useState } from "react";
import { motion } from "framer-motion";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { useAccount } from "wagmi";
import { formatEther, parseEther } from "viem";
import {
    ArrowDownUp, Loader2, Check, AlertTriangle, ArrowUpRight, Settings,
} from "lucide-react";
import {
    useSwapQuote, useBalances, useApprovePiggyForRouter, useSwap,
    usePairReserves, autoSlippageBps,
    type SwapDirection,
} from "@/hooks/useSwap";
import { ADDRESSES, CHAIN_ID, EXPLORER_TX } from "@/lib/config";
import { fmtCompact } from "@/lib/format";
import { PiggyMark, EthMark } from "@/components/Logo";
import { DexScreenerChart } from "@/components/DexScreenerChart";

const SLIPPAGES = [100, 300, 500, 1000]; // manual-override presets, bps
const LAUNCH_GLIDE_HOURS = 48;

export default function Swap() {
    const { isConnected, chainId } = useAccount();
    const wrongChain = isConnected && chainId !== CHAIN_ID;
    const noRouter = !ADDRESSES.DEX_ROUTER || !ADDRESSES.PIGGY_TOKEN || !ADDRESSES.WNATIVE;

    const [direction, setDirection] = useState<SwapDirection>("buy");
    const [inputStr, setInputStr] = useState("");
    // Slippage is AUTO by default (sized to live liquidity so swaps don't fail
    // and the user never has to touch it). null = auto; a number = manual override.
    const [manualSlippageBps, setManualSlippageBps] = useState<number | null>(null);
    const [showSettings, setShowSettings] = useState(false);

    const amountIn = safeParse(inputStr);
    const { amountOut: grossOut, taxBps } = useSwapQuote(direction, amountIn);
    const balances = useBalances();
    const reserves = usePairReserves();

    const autoBps = autoSlippageBps({ direction, amountIn, grossOut, taxBps, reserves });
    const slippageBps = manualSlippageBps ?? autoBps;
    const isAuto = manualSlippageBps === null;

    // BUY: getAmountsOut returns the pre-tax PIGGY output. Tax fires when
    // PIGGY leaves the pair to the buyer, so user receives gross * (1 - tax).
    // SELL: useSwapQuote already feeds the router a tax-adjusted amountIn
    // (audit fix H8), so its return value IS the ETH the user receives.
    // taxBps is read live from the token, so the elevated launch-glide sell
    // tax (8%/6%) is reflected accurately instead of a hardcoded 4%.
    const expectedOut = direction === "buy"
        ? (grossOut * (10_000n - taxBps)) / 10_000n
        : grossOut;
    const minOut = (expectedOut * BigInt(10_000 - slippageBps)) / 10_000n;

    const inputBalance = direction === "buy" ? balances.eth : balances.piggy;
    const overBalance = amountIn > inputBalance;
    const needsApproval = direction === "sell" && amountIn > 0n && balances.allowanceToRouter < amountIn;

    const approve = useApprovePiggyForRouter();
    const swap = useSwap();
    const approveBusy = approve.status === "pending" || approve.status === "confirming";
    const swapBusy = swap.status === "pending" || swap.status === "confirming";

    const inputSymbol = direction === "buy" ? "ETH" : "PIGGY";
    const outputSymbol = direction === "buy" ? "PIGGY" : "ETH";

    const handleSwap = () => {
        if (direction === "buy") swap.buy(amountIn, minOut);
        else swap.sell(amountIn, minOut);
    };

    return (
        <>
        <section className="max-w-xl mx-auto px-6 py-16">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center mb-10"
            >
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-pink mb-4 text-xs font-semibold text-piggy-pink-200">
                    <ArrowDownUp className="w-3 h-3" />
                    Routed through Uniswap
                </div>
                <h1 className="display text-5xl font-black tracking-tighter mb-3">
                    Swap <span className="gradient-text-shimmer">PIGGY</span>
                </h1>
                <p className="text-piggy-cream/60 text-sm max-w-md mx-auto">
                    No toll-dodging in Sherwood — the tax fires at the token level
                    on every registered pair. Same 4% whether you use this UI,
                    Uniswap directly, or any aggregator.
                </p>
            </motion.div>

            {noRouter ? (
                <NoticeBox>
                    Pair not yet live. Set <code>VITE_PIGGY_TOKEN_ADDRESS</code>,{" "}
                    <code>VITE_WNATIVE</code>, and <code>VITE_DEX_ROUTER</code> in your .env.
                </NoticeBox>
            ) : (
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="glass rounded-3xl p-6"
                >
                    <div className="flex items-center justify-between mb-5">
                        <div className="text-xs font-semibold uppercase tracking-wider text-piggy-cream/50">
                            {direction === "buy" ? "Buy PIGGY" : "Sell PIGGY"}
                        </div>
                        <button
                            onClick={() => setShowSettings((s) => !s)}
                            className="w-8 h-8 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] grid place-items-center"
                            aria-label="Settings"
                        >
                            <Settings className="w-4 h-4 text-piggy-cream/60" />
                        </button>
                    </div>

                    {showSettings && (
                        <div className="rounded-2xl bg-white/[0.03] p-4 mb-5">
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-xs font-semibold text-piggy-cream/60">
                                    Slippage tolerance
                                </div>
                                <div className="text-xs text-piggy-cream/40">
                                    {isAuto ? `Auto · ${(slippageBps / 100).toFixed(1)}%` : "Manual"}
                                </div>
                            </div>
                            <div className="grid grid-cols-5 gap-2">
                                <button
                                    onClick={() => setManualSlippageBps(null)}
                                    className={`px-3 py-2 rounded-xl text-xs font-semibold ${
                                        isAuto
                                            ? "bg-piggy-pink-500/20 text-piggy-pink-200 ring-1 ring-piggy-pink-500/40"
                                            : "bg-white/[0.04] text-piggy-cream/60 hover:bg-white/[0.07]"
                                    }`}
                                >
                                    Auto
                                </button>
                                {SLIPPAGES.map((bps) => (
                                    <button
                                        key={bps}
                                        onClick={() => setManualSlippageBps(bps)}
                                        className={`px-3 py-2 rounded-xl text-xs font-semibold ${
                                            !isAuto && slippageBps === bps
                                                ? "bg-piggy-pink-500/20 text-piggy-pink-200 ring-1 ring-piggy-pink-500/40"
                                                : "bg-white/[0.04] text-piggy-cream/60 hover:bg-white/[0.07]"
                                        }`}
                                    >
                                        {bps / 100}%
                                    </button>
                                ))}
                            </div>
                            <div className="text-xs text-piggy-cream/40 mt-2">
                                Auto sizes slippage to current liquidity so your swap
                                won't fail — the PIGGY tax is handled separately. Only
                                override if you know what you're doing.
                            </div>
                        </div>
                    )}

                    <SwapBox
                        label="You pay"
                        symbol={inputSymbol}
                        value={inputStr}
                        onChange={setInputStr}
                        balance={inputBalance}
                        editable
                    />

                    <div className="flex justify-center -my-2 relative z-10">
                        <button
                            onClick={() => {
                                setDirection((d) => (d === "buy" ? "sell" : "buy"));
                                setInputStr("");
                            }}
                            className="w-10 h-10 rounded-2xl bg-piggy-violet ring-4 ring-piggy-black grid place-items-center text-piggy-cream/80 hover:text-piggy-cream hover:scale-110 transition-all"
                            aria-label="Flip direction"
                        >
                            <ArrowDownUp className="w-4 h-4" />
                        </button>
                    </div>

                    <SwapBox
                        label={`You receive (after ${Number(taxBps) / 100}% tax)`}
                        symbol={outputSymbol}
                        value={expectedOut > 0n ? formatEther(expectedOut) : ""}
                        balance={direction === "buy" ? balances.piggy : balances.eth}
                    />

                    <div className="mt-4 space-y-1 text-xs text-piggy-cream/50">
                        <Row label="Gross quote (router)"
                             value={grossOut > 0n ? `${formatEther(grossOut).slice(0, 12)} ${outputSymbol}` : "—"} />
                        <Row label="PIGGY tax"
                             value={amountIn > 0n ? `${Number(taxBps) / 100}% (taken on transfer)` : "—"} />
                        <Row label="Slippage"
                             value={amountIn > 0n ? `${(slippageBps / 100).toFixed(1)}%${isAuto ? " (auto)" : ""}` : "—"} />
                        <Row label="Min received (after slippage)"
                             value={minOut > 0n ? `${formatEther(minOut).slice(0, 12)} ${outputSymbol}` : "—"} />
                    </div>

                    <div className="mt-5">
                        {!isConnected ? (
                            <ConnectButton.Custom>
                                {({ openConnectModal }) => (
                                    <button onClick={openConnectModal} className="w-full btn-primary justify-center">
                                        Connect wallet to swap
                                    </button>
                                )}
                            </ConnectButton.Custom>
                        ) : wrongChain ? (
                            <ConnectButton.Custom>
                                {({ openChainModal }) => (
                                    <button onClick={openChainModal} className="w-full btn-primary justify-center">
                                        Switch to Robinhood Chain
                                    </button>
                                )}
                            </ConnectButton.Custom>
                        ) : needsApproval ? (
                            <button
                                onClick={approve.approve}
                                disabled={approveBusy}
                                className="w-full btn-primary justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                            >
                                {approveBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                                {approveBusy ? "Approving..." : "Approve PIGGY"}
                            </button>
                        ) : (
                            <button
                                onClick={handleSwap}
                                disabled={swapBusy || amountIn === 0n || overBalance || expectedOut === 0n}
                                className="w-full btn-primary justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                            >
                                {swapBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                                {swap.status === "confirmed" && <Check className="w-4 h-4" />}
                                {swapBusy
                                    ? "Confirming..."
                                    : swap.status === "confirmed"
                                    ? "Swapped!"
                                    : overBalance
                                    ? `Insufficient ${inputSymbol}`
                                    : amountIn === 0n
                                    ? "Enter an amount"
                                    : direction === "buy"
                                    ? "Buy PIGGY"
                                    : "Sell PIGGY"}
                            </button>
                        )}
                    </div>

                    <TxLink hash={approve.hash || swap.hash} />

                    <GlideNotice />
                </motion.div>
            )}
            </section>

            <motion.section
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5 }}
                className="max-w-5xl mx-auto px-6 pb-20"
            >
                <DexScreenerChart />
            </motion.section>
        </>
    );
}

function SwapBox({
    label, symbol, value, onChange, balance, editable,
}: {
    label: string;
    symbol: string;
    value: string;
    onChange?: (s: string) => void;
    balance: bigint;
    editable?: boolean;
}) {
    return (
        <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
            <div className="flex justify-between text-xs text-piggy-cream/50 mb-2">
                <span>{label}</span>
                <span>
                    Balance: {fmtCompact(Number(balance) / 1e18, 4)}{" "}
                    {editable && balance > 0n && (
                        <button
                            onClick={() => onChange?.(formatEther(balance))}
                            className="text-piggy-pink-300 hover:text-piggy-pink-200 font-semibold ml-1"
                        >
                            MAX
                        </button>
                    )}
                </span>
            </div>
            <div className="flex items-center gap-3">
                {editable ? (
                    <input
                        type="text"
                        inputMode="decimal"
                        value={value}
                        onChange={(e) => {
                            const s = e.target.value.replace(/[^0-9.]/g, "");
                            if ((s.match(/\./g) ?? []).length > 1) return;
                            onChange?.(s);
                        }}
                        placeholder="0.0"
                        // Strip every browser default that can offset the text
                        // box horizontally / vertically vs the static <div> on
                        // the receive side. Without `appearance: none` and
                        // explicit text-indent: 0, the input's text starts a
                        // few pixels right of the div's text — the chip then
                        // looks shifted relative to its PIGGY counterpart.
                        className="flex-1 bg-transparent outline-none border-0 p-0 m-0 leading-none text-3xl font-bold display text-piggy-cream"
                        style={{
                            appearance: "none",
                            WebkitAppearance: "none",
                            textIndent: 0,
                            boxShadow: "none",
                            minWidth: 0,
                        }}
                    />
                ) : (
                    <div className="flex-1 leading-none text-3xl font-bold display text-piggy-cream overflow-hidden text-ellipsis">
                        {value ? value.slice(0, 12) : "0.0"}
                    </div>
                )}
                <TokenChip symbol={symbol} />
            </div>
        </div>
    );
}

function TokenChip({ symbol }: { symbol: string }) {
    return (
        <div className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-white/[0.04] border border-white/10">
            {symbol === "PIGGY"
                ? <PiggyMark size={20} className="rounded-lg" />
                : <EthMark size={20} />}
            <span className="display font-bold text-piggy-cream">{symbol}</span>
        </div>
    );
}

function Row({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex justify-between">
            <span>{label}</span>
            <span className="text-piggy-cream/80 tabular-nums">{value}</span>
        </div>
    );
}

function GlideNotice() {
    // We can't read launchTimestamp from chain without piggy ABI surface; show as a static reminder.
    return (
        <div className="mt-5 rounded-2xl bg-piggy-pink-500/[0.08] border border-piggy-pink-500/20 p-3 text-xs text-piggy-cream/70">
            <span className="text-piggy-cream font-semibold">First {LAUNCH_GLIDE_HOURS}h post-launch:</span>{" "}
            sell tax is elevated (8% in hours 0-24, 6% in hours 24-48) to discourage launch dumping.
            Buy tax stays at 4% throughout. Steady-state 4% kicks in after {LAUNCH_GLIDE_HOURS}h.
        </div>
    );
}

function safeParse(input: string): bigint {
    try {
        if (!input || input === ".") return 0n;
        return parseEther(input);
    } catch {
        return 0n;
    }
}

function TxLink({ hash }: { hash?: `0x${string}` }) {
    if (!hash) return null;
    return (
        <a
            href={`${EXPLORER_TX}/${hash}`}
            target="_blank" rel="noopener noreferrer"
            className="mt-3 flex items-center justify-center gap-1 text-xs text-piggy-cream/50 hover:text-piggy-cream"
        >
            View on Explorer <ArrowUpRight className="w-3 h-3" />
        </a>
    );
}

function NoticeBox({ children }: { children: React.ReactNode }) {
    return (
        <div className="glass-pink rounded-3xl p-6 flex gap-3 items-start max-w-2xl mx-auto">
            <AlertTriangle className="w-5 h-5 text-piggy-pink-300 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-piggy-cream/80">{children}</div>
        </div>
    );
}
